/**
 * Admin "Create Gift QR" form. Mirrors `app/owner/subscriptions/qr-codes/new.tsx`
 * with admin impersonation.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import Card from '../../../../../src/components/Card';
import QrSvgView from '../../../../../src/components/QrSvgView';
import Button from '../../../../../src/components/Button';
import Input from '../../../../../src/components/Input';
import FreeUpsellPicker from '../../../../../src/components/upsells/FreeUpsellPicker';
import type { UpsellCatalogItem } from '../../../../../src/components/upsells/UpsellPolicyPicker';
import Picker from '../../../../../src/components/Picker';
import { useToast } from '../../../../../src/components/Toast';
import ContentContainer from '../../../../../src/components/ContentContainer';
import { useTheme } from '../../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../../src/utils/theme';
import { makeOwnerApi } from '../../../../../src/api/adminAsOwner';

interface Location { id: string; name: string }
interface WashTier { id: string; name: string; priceInCents: number; isActive: boolean }

type Mode = 'single' | 'multi';

export default function AdminCreateGiftQRScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { id: ownerId } = useLocalSearchParams<{ id: string }>();
  const ownerApi = useMemo(() => makeOwnerApi(ownerId ?? ''), [ownerId]);

  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);

  const [mode, setMode] = useState<Mode>('multi');
  const [discountKind, setDiscountKind] = useState<'free_wash' | 'percent' | 'free_minutes' | 'free_vend' | 'credit' | 'free_upsell'>('percent');
  const [percentOff, setPercentOff] = useState('25');
  // 1.4 R05 (admin twin of the owner form)
  const [discountScope, setDiscountScope] = useState<'wash' | 'wash_and_upsells'>('wash');
  const [freeUpsellIds, setFreeUpsellIds] = useState<string[]>([]);
  const [upsellCatalog, setUpsellCatalog] = useState<UpsellCatalogItem[]>([]);
  // Free minutes (1–240) — scope is self-serve and/or vacuums only.
  const [freeMinutes, setFreeMinutes] = useState('10');
  const [minutesSelfServe, setMinutesSelfServe] = useState(true);
  const [minutesVacuum, setMinutesVacuum] = useState(false);
  // Free vending credit in dollars ($0.25–$100 → freeVendCents).
  const [freeVendAmount, setFreeVendAmount] = useState('2');
  // Gift credit in dollars ($1–$500 → creditCents) — deposited instantly at claim.
  const [creditAmount, setCreditAmount] = useState('10');
  const [tierId, setTierId] = useState('any');
  const [locationId, setLocationId] = useState<string>('any');
  const [totalCap, setTotalCap] = useState('50');
  const [expiresInDays, setExpiresInDays] = useState('30');

  const [createdSvg, setCreatedSvg] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  useEffect(() => {
    if (!ownerId) return;
    const ac = new AbortController();
    (async () => {
      try {
        const locs = await ownerApi.get<Location[]>('/api/locations');
        if (ac.signal.aborted) return;
        setLocations(locs);
        const catalog = await ownerApi.get<WashTier[]>('/api/wash-tiers');
        if (ac.signal.aborted) return;
        setWashTiers(catalog.filter(t => t.isActive));
        ownerApi.get<UpsellCatalogItem[]>('/api/upsells').then(list => { if (!ac.signal.aborted) setUpsellCatalog(list.filter(u => u.isActive !== false)); }).catch(() => {});
      } catch (err: any) {
        if (!ac.signal.aborted) toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
    return () => ac.abort();
  }, [ownerId]);

  const handleSubmit = async () => {
    const limit = mode === 'single' ? 1 : Math.max(2, parseInt(totalCap) || 50);
    if (mode === 'multi' && (!totalCap || parseInt(totalCap) <= 1)) {
      toast.show({ type: 'error', title: 'Error', message: 'Multi-use cap must be 2 or more' });
      return;
    }
    if (discountKind === 'percent') {
      const pct = parseInt(percentOff);
      if (!pct || pct < 1 || pct > 100) {
        toast.show({ type: 'error', title: 'Error', message: 'Percent must be 1–100' });
        return;
      }
    }
    if (discountKind === 'free_minutes') {
      const mins = parseInt(freeMinutes);
      if (!mins || mins < 1 || mins > 240) {
        toast.show({ type: 'error', title: 'Error', message: 'Free minutes must be 1–240' });
        return;
      }
      if (!minutesSelfServe && !minutesVacuum) {
        toast.show({ type: 'error', title: 'Error', message: 'Pick self-serve bays, vacuums, or both' });
        return;
      }
    }
    if (discountKind === 'free_vend') {
      const cents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
      if (cents < 25 || cents > 10000) {
        toast.show({ type: 'error', title: 'Error', message: 'Vending credit must be $0.25–$100' });
        return;
      }
    }
    if (discountKind === 'credit') {
      const cents = Math.round((parseFloat(creditAmount) || 0) * 100);
      if (cents < 100 || cents > 50000) {
        toast.show({ type: 'error', title: 'Error', message: 'Credit must be $1–$500' });
        return;
      }
    }
    const days = parseInt(expiresInDays);
    if (!days || days < 1) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter valid expiration days' });
      return;
    }

    try {
      setSubmitting(true);
      const body: any = {
        kind: 'promo',
        discountKind,
        locationId: locationId === 'any' ? null : locationId,
        redemptionLimit: limit,
        perCustomerLimit: 1,
        expiresInDays: days,
        source: mode === 'single' ? 'gift_qr_single' : 'gift_qr_multi',
      };
      if (discountKind === 'free_minutes') {
        body.freeMinutes = parseInt(freeMinutes);
        body.appliesTo = { tierIds: [], selfServe: minutesSelfServe, vacuum: minutesVacuum };
      } else if (discountKind === 'free_vend') {
        // Vending-only scope is mandatory server-side for free_vend.
        body.freeVendCents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
        body.appliesTo = { tierIds: [], selfServe: false, vending: true };
      } else if (discountKind === 'credit') {
        // Scope-free: balance dollars deposited at claim, spendable anywhere.
        body.creditCents = Math.round((parseFloat(creditAmount) || 0) * 100);
        body.locationId = null;
      } else {
        body.percentOff = discountKind === 'percent' ? parseInt(percentOff) : discountKind === 'free_wash' ? 100 : undefined;
        if (discountKind === 'percent') body.discountScope = discountScope;
        if (freeUpsellIds.length && discountKind !== 'percent') body.freeUpsellIds = freeUpsellIds;
        if (discountKind === 'free_upsell' && freeUpsellIds.length === 0) { toast.show({ type: 'error', title: 'Error', message: 'Pick at least one free add-on' }); setSubmitting(false); return; }
        body.bayType = 'automatic';
        body.tierId = tierId === 'any' ? null : tierId;
      }
      const created = await ownerApi.post<{ id: string; code: string }>('/api/vouchers', body);
      const qr = await ownerApi.get<{ svg: string; redemptionUrl: string; code: string }>(`/api/vouchers/${created.id}/qr`);
      setCreatedSvg(qr.svg);
      setCreatedUrl(qr.redemptionUrl);
      setCreatedCode(qr.code);
      toast.show({ type: 'success', title: 'QR created' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const tierOptions = [{ label: 'Any tier', value: 'any' }, ...washTiers.map(t => ({ label: `${t.name} ($${(t.priceInCents / 100).toFixed(2)})`, value: t.id }))];
  const locationOptions = [{ label: 'Any location', value: 'any' }, ...locations.map(l => ({ label: l.name, value: l.id }))];

  if (createdSvg) {
    return (
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer>
          <Card style={[styles.formCard, { alignItems: 'center', padding: spacing.lg }]}>
            <Text style={styles.sectionTitle}>QR ready</Text>
            <View style={{ alignItems: 'center', marginVertical: spacing.md }}>
              <QrSvgView uri={createdSvg} width={320} accessibilityLabel="Gift QR code" />
            </View>
            <Text style={styles.hintText}>Code: {createdCode}</Text>
            <Text style={[styles.hintText, { fontSize: fontSize.xs }]}>Link: {createdUrl}</Text>
            <Button title="Done" onPress={() => router.back()} size="lg" style={{ marginTop: spacing.lg, width: '100%' }} />
          </Card>
          <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Text style={styles.adminBanner}>Admin acting on behalf of owner {ownerId}</Text>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>QR Type</Text>
          <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
            {(['single', 'multi'] as Mode[]).map(m => (
              <TouchableOpacity key={m} onPress={() => setMode(m)}
                style={[styles.chip, mode === m && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                <Text style={[styles.chipText, mode === m && { color: '#fff' }]}>
                  {m === 'single' ? 'Single-use' : 'Multi-use (1 per customer)'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {mode === 'multi' && (
            <Input label="Total redemptions allowed" value={totalCap} onChangeText={setTotalCap} placeholder="50" keyboardType="number-pad" />
          )}
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Discount</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
            {([['percent', 'Percent off'], ['free_wash', 'Free wash'], ['free_upsell', 'Free add-on with a wash'], ['free_minutes', 'Free minutes'], ['free_vend', 'Free vending credit'], ['credit', 'Credit $']] as const).map(([k, label]) => (
              <TouchableOpacity key={k} onPress={() => setDiscountKind(k)}
                style={[styles.chip, discountKind === k && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                <Text style={[styles.chipText, discountKind === k && { color: '#fff' }]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {discountKind === 'percent' && (
            <>
              <Input label="Percent off (1–100)" value={percentOff} onChangeText={setPercentOff} placeholder="25" keyboardType="number-pad" />
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
                {([['wash', 'The wash only'], ['wash_and_upsells', 'The wash and upsells']] as const).map(([v, label]) => (
                  <TouchableOpacity key={v} onPress={() => setDiscountScope(v)} style={[styles.chip, discountScope === v && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                    <Text style={[styles.chipText, discountScope === v && { color: '#fff' }]}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
          {(discountKind === 'free_wash' || discountKind === 'free_upsell') && (
            <FreeUpsellPicker catalog={upsellCatalog} selected={freeUpsellIds} onChange={setFreeUpsellIds} title={discountKind === 'free_upsell' ? 'Free upsells' : 'Also free with this wash (optional)'} />
          )}
          {discountKind === 'free_minutes' && (
            <>
              <Input label="Free minutes (1–240)" value={freeMinutes} onChangeText={setFreeMinutes} placeholder="10" keyboardType="number-pad" />
              <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                <TouchableOpacity onPress={() => setMinutesSelfServe(v => !v)}
                  style={[styles.chip, minutesSelfServe && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                  <Text style={[styles.chipText, minutesSelfServe && { color: '#fff' }]}>Self-serve bays</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setMinutesVacuum(v => !v)}
                  style={[styles.chip, minutesVacuum && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                  <Text style={[styles.chipText, minutesVacuum && { color: '#fff' }]}>Vacuums</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
          {discountKind === 'free_vend' && (
            <>
              <Input label="Vending credit ($0.25–$100)" value={freeVendAmount} onChangeText={setFreeVendAmount} placeholder="2" keyboardType="decimal-pad" />
              <Text style={styles.hintText}>Vending machines only — scope is forced server-side.</Text>
            </>
          )}
          {discountKind === 'credit' && (
            <>
              <Input label="Gift credit ($1–$500)" value={creditAmount} onChangeText={setCreditAmount} placeholder="10" keyboardType="decimal-pad" />
              <Text style={styles.hintText}>
                Each customer who scans and claims gets the credit instantly, up to the redemption limit. Spendable anywhere — no scope.
              </Text>
            </>
          )}
        </Card>

        {discountKind !== 'credit' && (
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Scope</Text>
          {(discountKind === 'percent' || discountKind === 'free_wash') && tierOptions.length > 1 && (
            <Picker label="Tier" options={tierOptions} value={tierId} onValueChange={setTierId} />
          )}
          <Picker label="Valid at" options={locationOptions} value={locationId} onValueChange={setLocationId} />
        </Card>
        )}

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Expiration</Text>
          <Input label="Expires in (days)" value={expiresInDays} onChangeText={setExpiresInDays} placeholder="30" keyboardType="number-pad" />
        </Card>

        <Button title="Create QR" onPress={handleSubmit} loading={submitting} size="lg" style={{ marginTop: spacing.md }} />
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  hintText: { fontSize: fontSize.sm, color: c.textLight, marginTop: spacing.xs, textAlign: 'center' },
  chip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  chipText: { fontSize: fontSize.sm, color: c.text },
  adminBanner: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginBottom: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
